import {Component, inject} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {DeviceProfileService} from '../../../../../../../services/device-profile.service';
import {ButtonIconComponent} from '../../../../../../../shared/components/button-icon/button-icon.component';
import {InputSelectComponent} from '../../../../../../../shared/components/input-select/input-select';
import {InputTextComponent} from '../../../../../../../shared/components/input-text/input-text.component';
import {TranslateSafePipe} from '../../../../../../../shared/pipes/translation.pipe';
import {
  AlarmRuleComparisonRuleConfig,
  AlarmRuleComparisonSideConfig,
  ComparisonSourceSide,
  ComparisonValueSourceType,
} from '../../../../../../../types/dtos/alarm-rule-condition.dto';
import {DeviceProfileDto} from '../../../../../../../types/dtos/deviceProfile.dto';
import {
  InputSelectInterface,
  InputSelectOptionInterface,
  InputTextInterface,
} from '../../../../../../../types/interfaces/input.interface';
import {BaseCondition} from '../base-condition';
import {BaseRuleCondition} from '../base-rule-condition';
import {ComparisonRuleCondition} from './comparison.rule-condition';

@Component({
  selector: 'app-comparison-condition',
  imports: [
    InputSelectComponent,
    InputTextComponent,
    ButtonIconComponent,
    TranslateSafePipe,
  ],
  templateUrl: './comparison-condition.html',
  styleUrls: [
    './comparison-condition.css',
    '../base-rule-condition.css',
  ],
})
export class ComparisonCondition extends BaseCondition {
  override ruleCondition: BaseRuleCondition = new ComparisonRuleCondition();
  private readonly _deviceProfileService: DeviceProfileService = inject(DeviceProfileService);

  constructor(
    private readonly _translateService: TranslateService
  ) {
    super();
  }

  public get getDefaultValueString(): string {
    const config = this.getConfig() as unknown as AlarmRuleComparisonRuleConfig | undefined;

    const leftText: string = config?.left ? this.getConfigText(config.left) : 'Unknown';
    const rightText: string = config?.right ? this.getConfigText(config.right) : 'Unknown';

    const operator: string = config?.operator
      ? this._translateService.instant('common.input.rule-operation.values.' + config.operator)
      : '?';

    return `${leftText}<br>${operator}<br>${rightText}`;
  }

  public getSourceType(side: ComparisonSourceSide): ComparisonValueSourceType | null {
    return this.formGroup.get(`${side}_source_type`)?.value ?? null;
  }

  public getMeasurementVariableInput(side: ComparisonSourceSide): InputSelectInterface {
    const deviceProfiles: DeviceProfileDto[] = this._deviceProfileService.getCurrentList();
    const options: InputSelectOptionInterface[] = [];

    deviceProfiles.forEach(deviceProfile => {
      (deviceProfile.variables ?? []).forEach(variable => {
        options.push({
          label: variable.display_name ?? variable.name,
          value: variable.name,
        });
      });
    });

    return {
      formGroup: this.formGroup,
      isTranslated: true,
      label: 'common.input.measurement-variable.label',
      required: true,
      id: `${side}_measurement_variable`,
      options,
      filter: true,
    };
  }

  public getSourceTypeInput(side: ComparisonSourceSide): InputSelectInterface {
    return {
      formGroup: this.formGroup,
      isTranslated: true,
      label: side === 'left' ? 'common.input.source-type.left.label' : 'common.input.source-type.right.label',
      required: true,
      id: `${side}_source_type`,
      options: Object.values(ComparisonValueSourceType)
        .map((status: ComparisonValueSourceType) => ({
          isTranslated: true,
          label: 'common.input.source-type.values.' + status,
          value: status,
        })),
    };
  }

  public getOperatorInput(): InputSelectInterface {
    return {
      formGroup: this.formGroup,
      isTranslated: true,
      label: 'common.input.rule-operation.label',
      required: true,
      id: 'ruleOperator',
      iconLeft: 'text_fields',
      options: [
        {
          isTranslated: true,
          label: 'common.input.rule-operation.values.lt',
          value: 'lt',
        },
        {
          isTranslated: true,
          label: 'common.input.rule-operation.values.lte',
          value: 'lte',
        },
        {
          isTranslated: true,
          label: 'common.input.rule-operation.values.gt',
          value: 'gt',
        },
        {
          isTranslated: true,
          label: 'common.input.rule-operation.values.gte',
          value: 'gte',
        },
        {
          isTranslated: true,
          label: 'common.input.rule-operation.values.eq',
          value: 'eq',
        },
        {
          isTranslated: true,
          label: 'common.input.rule-operation.values.neq',
          value: 'neq',
        },
      ],
    };
  }

  public getFixedValueInput(side: ComparisonSourceSide): InputTextInterface {
    return {
      formGroup: this.formGroup,
      isTranslated: true,
      label: 'common.input.fixe-value.label',
      required: true,
      id: `${side}_fixed_value`,
      type: 'number',
      iconLeft: 'pin',
    };
  }

  public getRangeHoursInput(side: ComparisonSourceSide): InputTextInterface {
    return {
      formGroup: this.formGroup,
      isTranslated: true,
      label: 'common.input.count_hours.label',
      required: true,
      id: `${side}_range_hours`,
      type: 'number',
      iconLeft: 'date_range',
      placeholder: 'z. B. 24',
      infoText: 'common.input.count_hours.info',
    };
  }

  public getValueCountInput(side: ComparisonSourceSide): InputTextInterface {
    return {
      formGroup: this.formGroup,
      isTranslated: true,
      label: 'common.input.count_value.label',
      required: true,
      id: `${side}_value_count`,
      type: 'number',
      iconLeft: 'calculate',
      placeholder: 'z. B. 3',
      infoText: 'common.input.count_value.info',
    };
  }

  private getConfigText(config: AlarmRuleComparisonSideConfig): string {
    let text: string = '';

    switch (config.source_type) {
      case ComparisonValueSourceType.CURRENT_VALUE:
        text += config.measurement_variable;
        break;
      case ComparisonValueSourceType.FIXED_VALUE:
        text += config.fixed_value;
        break;
      case ComparisonValueSourceType.HISTORICAL_VALUE:
      case ComparisonValueSourceType.DELTA_VALUE:
      case ComparisonValueSourceType.TIME_RANGE_SUM:
        text += config.measurement_variable + ' - ' + config.range_hours;
        text += ' ' + this._translateService.instant('common.input.count_hours.label');
        break;
      case ComparisonValueSourceType.LAST_N_VALUES_SUM:
      case ComparisonValueSourceType.LAST_N_VALUES_AVG:
      case ComparisonValueSourceType.LAST_N_VALUES_MIN:
      case ComparisonValueSourceType.LAST_N_VALUES_MAX:
        text += config.measurement_variable + ' - ' + config.value_count;
        text += ' ' + this._translateService.instant('common.input.count_value.label');
        break;
    }

    text += ' (' + this._translateService.instant('common.input.source-type.values.' + config.source_type) + ')';

    return text;
  }

}
